/**
 * /app/billing — plan management.
 *
 * Current tier card + plan comparison cards + manage-billing button. No
 * Stripe SDK interaction on the client — everything goes through the backend
 * and returns a hosted redirect URL.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { CheckIcon, ExternalLinkIcon, SparklesIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError, api } from '@/lib/api';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { cn } from '@/lib/utils';

type Interval = 'monthly' | 'annual';
type PaidTier = 'starter' | 'growth' | 'scale';

interface PlanCopy {
  tier: PaidTier;
  name: string;
  tagline: string;
  monthly: number;
  annual: number;
  bullets: string[];
  highlight?: boolean;
}

const PLANS: PlanCopy[] = [
  {
    tier: 'starter',
    name: 'Starter',
    tagline: 'For small API teams getting started.',
    monthly: 499,
    annual: 4990,
    bullets: [
      '1 OpenAPI spec monitored',
      '2 SDK repos watched',
      'Monthly re-audit',
      'Generated llms.txt + Agent Skills',
      'Email support (2 business days)',
    ],
  },
  {
    tier: 'growth',
    name: 'Growth',
    tagline: 'For API-first companies shipping daily.',
    monthly: 1999,
    annual: 19990,
    bullets: [
      '5 specs, 10 SDK repos, 3 docs domains',
      '500 monitored PRs / month',
      'PR-comment bot',
      'Generated MCP server, Cursor rules, Copilot instructions',
      'Weekly drift + monthly benchmark PDF',
      'Shared Slack support (1 business day)',
    ],
    highlight: true,
  },
  {
    tier: 'scale',
    name: 'Scale',
    tagline: 'For platform teams treating AI agents as a surface.',
    monthly: 4999,
    annual: 49990,
    bullets: [
      'Unlimited specs, repos, docs',
      '5,000 monitored PRs / month',
      'Custom PR-bot rules',
      'Custom asset templates',
      'Public API access',
      'Quarterly review call + dedicated Slack (4h SLA)',
    ],
  },
];

export function BillingPage() {
  const { data: perms } = usePermissions();
  const queryClient = useQueryClient();
  const { location } = useRouterState();
  const search = new URLSearchParams(location.searchStr ?? '');
  const banner = search.get('status');

  const [interval, setInterval] = useState<Interval>('monthly');

  // After a successful redirect, the Stripe webhook lands within seconds but
  // not instantly — poll permissions every 1.5s for ~12s so the UI flips
  // without requiring a refresh.
  useEffect(() => {
    if (banner !== 'success') return;
    let tries = 0;
    const handle = window.setInterval(() => {
      tries += 1;
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
      if (tries >= 8) window.clearInterval(handle);
    }, 1500);
    return () => window.clearInterval(handle);
  }, [banner, queryClient]);

  const checkout = useMutation({
    mutationFn: ({ tier, interval: iv }: { tier: PaidTier; interval: Interval }) =>
      api<{ url: string }>('/api/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ tier, interval: iv }),
      }),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
  });

  const portal = useMutation({
    mutationFn: () =>
      api<{ url: string }>('/api/billing/portal', {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
  });

  const currentTier = perms?.tier ?? 'free';
  const hasPaidSub = currentTier !== 'free';

  const errorMsg = useMemo(() => {
    const err = (checkout.error ?? portal.error) as ApiError | Error | undefined;
    if (!err) return null;
    if (err instanceof ApiError) {
      const detail = err.detail as { message?: string; error?: string } | undefined;
      return detail?.message ?? detail?.error ?? `Error ${err.status}`;
    }
    return err.message;
  }, [checkout.error, portal.error]);

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <header className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight">Billing</h1>
        <p className="mt-1 text-sm text-slate-500">Pick the plan that matches your surface area.</p>
      </header>

      {banner === 'success' && (
        <Banner tone="success">
          Thanks — your subscription is processing. This page will refresh in a few seconds.
        </Banner>
      )}
      {banner === 'cancelled' && (
        <Banner tone="warning">
          Checkout cancelled. Your plan hasn't changed.
        </Banner>
      )}
      {errorMsg && <Banner tone="critical">{errorMsg}</Banner>}

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Current plan</CardTitle>
          <CardDescription>
            You're on <strong className="uppercase tracking-wide">{currentTier}</strong>
            {perms?.caps && (
              <>
                {' · '}
                <span>
                  {formatLimit(perms.caps.specs)} specs ·{' '}
                  {formatLimit(perms.caps.sdk_repos)} SDK repos ·{' '}
                  {formatLimit(perms.caps.monitored_prs_per_month)} PRs/mo
                </span>
              </>
            )}
          </CardDescription>
        </CardHeader>
        {hasPaidSub && (
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => portal.mutate()} disabled={portal.isPending}>
              <ExternalLinkIcon className="h-4 w-4" />
              {portal.isPending ? 'Opening…' : 'Manage billing'}
            </Button>
            <span className="text-xs text-slate-500">
              Update payment method, view invoices, cancel.
            </span>
          </div>
        )}
      </Card>

      <div className="mb-6 flex items-center justify-end gap-2">
        <IntervalToggle value={interval} onChange={setInterval} />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {PLANS.map((plan) => (
          <PlanCard
            key={plan.tier}
            plan={plan}
            interval={interval}
            isCurrent={currentTier === plan.tier}
            onChoose={() => checkout.mutate({ tier: plan.tier, interval })}
            disabled={checkout.isPending}
          />
        ))}
      </div>

      <p className="mt-8 text-center text-xs text-slate-500">
        Need SSO, SOC 2, self-hosting, or custom terms? <a href="mailto:shashank@momentumai.dev" className="underline">Contact sales</a> for Enterprise (from $25K/yr).
      </p>
    </div>
  );
}

function IntervalToggle({
  value,
  onChange,
}: {
  value: Interval;
  onChange: (v: Interval) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-slate-200 bg-white p-1 text-sm dark:border-slate-800 dark:bg-slate-900">
      {(['monthly', 'annual'] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={cn(
            'rounded px-3 py-1 transition-colors',
            value === v
              ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
              : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100',
          )}
        >
          {v === 'monthly' ? 'Monthly' : 'Annual'}
          {v === 'annual' && (
            <span className="ml-1.5 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
              2 months free
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

function PlanCard({
  plan,
  interval,
  isCurrent,
  onChoose,
  disabled,
}: {
  plan: PlanCopy;
  interval: Interval;
  isCurrent: boolean;
  onChoose: () => void;
  disabled: boolean;
}) {
  const amount = interval === 'monthly' ? plan.monthly : plan.annual;
  const unit = interval === 'monthly' ? '/mo' : '/yr';

  return (
    <Card
      className={cn(
        'relative flex flex-col',
        plan.highlight && 'border-[var(--color-accent)] shadow-sm ring-1 ring-[var(--color-accent)]/20',
      )}
    >
      {plan.highlight && (
        <div className="absolute -top-3 right-4 flex items-center gap-1 rounded-full bg-[var(--color-accent)] px-3 py-1 text-xs font-medium text-white">
          <SparklesIcon className="h-3 w-3" /> Most popular
        </div>
      )}
      <CardHeader>
        <CardTitle>{plan.name}</CardTitle>
        <CardDescription>{plan.tagline}</CardDescription>
      </CardHeader>
      <div className="flex items-baseline gap-1">
        <span className="text-4xl font-semibold tracking-tight">${amount.toLocaleString()}</span>
        <span className="text-sm text-slate-500">{unit}</span>
      </div>
      <ul className="mt-5 flex-1 space-y-2 text-sm">
        {plan.bullets.map((b) => (
          <li key={b} className="flex gap-2">
            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-success)]" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6">
        {isCurrent ? (
          <Button variant="outline" className="w-full" disabled>
            Current plan
          </Button>
        ) : (
          <Button
            className="w-full"
            variant={plan.highlight ? 'default' : 'outline'}
            onClick={onChoose}
            disabled={disabled}
          >
            {disabled ? 'Opening Checkout…' : `Upgrade to ${plan.name}`}
          </Button>
        )}
      </div>
    </Card>
  );
}

function Banner({
  tone,
  children,
}: {
  tone: 'success' | 'warning' | 'critical';
  children: React.ReactNode;
}) {
  const classes = {
    success: 'border-[var(--color-success)] bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
    warning: 'border-[var(--color-warning)] bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
    critical: 'border-[var(--color-critical)] bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-200',
  }[tone];
  return (
    <div className={cn('mb-6 rounded-md border p-3 text-sm', classes)} role="status" aria-live="polite">
      {children}
    </div>
  );
}

function formatLimit(n: number): string {
  return n === -1 ? 'Unlimited' : n.toLocaleString();
}
